/** Reine Logik für Schritt 4 (Kandidatenliste) und den Drawer — ohne React, testbar. */

import {
  CandidateFilter,
  CandidatePersona,
  Judgement,
  WizardAnalysisForecast,
  WizardBlacklistList,
  WizardCandidate,
  WizardCandidateList,
  WizardState,
} from '../../campaign-wizard.types';

/** A = Muss erfüllt, B = Muss nicht (voll) belegt ohne Ausschluss, C = Ausschlusskriterium trifft zu (Produktionsregel). */
export const JUDGEMENTS: Judgement[] = ['A', 'B', 'C'];

/** Farbe der Urteils-Pille (Design `JUDGE`). */
export const JUDGE_COLORS: Record<Judgement, string> = {
  A: '#daf696',
  B: '#cfd6fb',
  C: '#f8d4d4',
};

export const JUDGE_LABEL_KEYS: Record<Judgement, string> = {
  A: 'wizListJudgeA',
  B: 'wizListJudgeB',
  C: 'wizListJudgeC',
};

export interface RatingPill {
  /** Übersetzungsschlüssel der Pille */
  labelKey: string;
  background: string;
  color?: string;
  /** Erklärende Zeile (Drawer/Tooltip): Übersetzungsschlüssel oder Klartext des Backends */
  hintKey?: string;
  hintText?: string;
  /** „Erneut bewerten“ ist sinnvoll */
  canRejudge: boolean;
}

/** D-11: Pille für den Bewertungszustand — Urteil, „Bewertung fehlgeschlagen“, „Wird analysiert“, „Wartet auf Analyse“, „Wird bewertet“. */
export const ratingPill = (
  c: Pick<WizardCandidate, 'judgement' | 'ratingState' | 'ratingError'>
): RatingPill => {
  if (c.judgement) {
    return {
      labelKey: JUDGE_LABEL_KEYS[c.judgement],
      background: JUDGE_COLORS[c.judgement],
      canRejudge: false,
    };
  }
  switch (c.ratingState) {
    case 'failed':
      return {
        labelKey: 'wizListRatingFailed',
        background: '#fee',
        color: '#940202',
        hintText: c.ratingError,
        hintKey: 'wizListRatingFailedHint',
        canRejudge: true,
      };
    case 'analysing':
      return {
        labelKey: 'wizListRatingAnalysing',
        background: '#E3E3E8',
        hintKey: 'wizListRatingAnalysingHint',
        canRejudge: false,
      };
    case 'waiting_quota':
      return {
        labelKey: 'wizListRatingQuota',
        background: '#fbe6c3',
        color: '#8E4101',
        hintKey: 'wizListRatingQuotaHint',
        canRejudge: true,
      };
    case 'waiting_linkedin':
      return {
        labelKey: 'wizListRatingLinkedin',
        background: '#fbe6c3',
        color: '#8E4101',
        hintKey: 'wizListRatingLinkedinHint',
        canRejudge: false,
      };
    default:
      return { labelKey: 'wizListJudgePending', background: '#E3E3E8', canRejudge: false };
  }
};

/** D-13: Tooltip der Pille — der volle Text zur kurzen Beschriftung. */
export const pillTitle = (
  c: Pick<WizardCandidate, 'judgement' | 'ratingState' | 'ratingError'>,
  t: (key: string) => string
): string | undefined => {
  const pill = ratingPill(c);
  if (c.judgement) return undefined;
  if (pill.hintText) return pill.hintText;
  if (pill.hintKey) return t(pill.hintKey);
  return undefined;
};

/** D-13: ein einziger Hinweis unter der Tabelle (Import, Analyse oder Kontingent). */
export const progressNoticeKey = (list: {
  importing?: boolean;
  analysisPending?: number;
  analysisQuotaExhausted?: boolean;
  analysisPause?: WizardCandidateList['analysisPause'];
}): {
  key: 'wizListAnalysisPause' | 'wizListAnalysisQuota' | 'wizListAnalysing' | 'wizListImporting';
  warn: boolean;
} | null => {
  if ((list.analysisPending ?? 0) > 0 && list.analysisPause) {
    return { key: 'wizListAnalysisPause', warn: true };
  }
  if ((list.analysisPending ?? 0) > 0 && list.analysisQuotaExhausted) {
    return { key: 'wizListAnalysisQuota', warn: true };
  }
  if ((list.analysisPending ?? 0) > 0) return { key: 'wizListAnalysing', warn: false };
  if (list.importing) return { key: 'wizListImporting', warn: false };
  return null;
};

/** Datum für den Hinweis („12. Okt.“), in der Zeitzone der Plattform; leer bei ungültigem Wert. */
export const pauseDate = (iso: string | undefined, language: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Berlin',
  }).format(date);
};

/** Hinweis „Profilanalyse pausiert“: Teilsätze nur, wenn die Daten vorhanden sind. */
export const analysisPauseText = (
  pause: NonNullable<WizardCandidateList['analysisPause']>,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string
): string => {
  const resumes = pauseDate(pause.resumesAt, language);
  const until = pauseDate(pause.reducedUntil, language);
  const half = pause.reductionFactor === undefined || pause.reductionFactor === 0.5;
  return [
    t('wizListPauseLead'),
    resumes ? t('wizListPauseResumes', { date: resumes }) : t('wizListPauseResumesAuto'),
    until ? t(half ? 'wizListPauseReducedHalf' : 'wizListPauseReduced', { date: until }) : '',
  ]
    .filter(Boolean)
    .join(' ');
};

/** Polling-Takt: solange pausiert, nur selten nachladen. */
export const CANDIDATE_POLL_PAUSED_MS = 60_000;

/** Zustand der Suche: Spinner nur bei `running` (F4). */
export const isSearchRunning = (
  list: Pick<WizardCandidateList, 'searchStatus' | 'importing'>
): boolean => (list.searchStatus ? list.searchStatus === 'running' : list.importing);

/** Polling: solange die Suche lädt oder Bewertungen/Analysen noch laufen (nicht bei „wartet auf Kontingent“). */
export const needsPolling = (list: WizardCandidateList): boolean =>
  isSearchRunning(list) ||
  list.items.some(
    item =>
      item.ratingState === 'pending' ||
      item.ratingState === 'analysing' ||
      item.ratingState === 'waiting_linkedin'
  );

/** Intervall des Pollings: 5 s normal, 60 s solange LinkedIn die Analyse begrenzt. */
export const pollIntervalMs = (list: WizardCandidateList, normalMs: number): number =>
  list.analysisPause ? CANDIDATE_POLL_PAUSED_MS : normalMs;

/** „Weitere ca. N Profile“: nur mit echter Zahl (Treffer der Suche minus bereits geladene), sonst kein Satz. */
export const remainingProfiles = (estimate: number | null, loaded: number): number | null =>
  estimate !== null && estimate !== undefined && estimate > loaded ? estimate - loaded : null;

/**
 * Hinweis unter der Liste, sobald die Vorschau geladen ist: nach dem Start lädt die Kampagne selbst nach; solange die Suchen
 * bei LinkedIn noch Treffer haben, lassen sich die nächsten 50 sofort holen. Während des Ladens (oder ohne Kandidaten) nichts.
 */
export const loadMoreView = (
  list: Pick<WizardCandidateList, 'total' | 'searchStatus' | 'importing' | 'moreAvailable'>
): { more: number } | null =>
  list.total > 0 && list.searchStatus === 'finished' && !list.importing
    ? { more: Math.max(0, list.moreAvailable ?? 0) }
    : null;

/** Was die Tabelle statt Zeilen zeigt, solange keine Kandidaten da sind. */
export type EmptyView =
  | 'loading'
  | 'failed'
  | 'none'
  | 'notStarted'
  | 'finished'
  | 'filtered'
  | null;

/**
 * Grund, wenn „Suche starten“ die Suche nicht angelegt hat (`started: false`, z. B. LinkedIn-Sitzung ungültig); sonst null.
 * Die Anfrage selbst gelingt (200), der Status der Liste bleibt „nicht gestartet“ – die Oberfläche zeigt „fehlgeschlagen“.
 */
export const startFailureOf = (res: { started?: boolean; message?: string } | null | undefined): string | null =>
  res && res.started === false ? res.message?.trim() || 'Die Suche konnte nicht gestartet werden.' : null;

/** Zusatzmeldung der Suche nur, wenn sie nicht schon als Fehlergrund dasteht (sonst erscheint derselbe Text zweimal). */
export const extraSearchMessage = (message: string | null, failure: string | null): string | null => {
  const m = message?.trim();
  if (!m) return null;
  const f = failure?.trim();
  if (f && (f.includes(m) || m.includes(f))) return null;
  return m;
};

export const emptyViewOf = (
  list: WizardCandidateList,
  shown: number,
  startFailure: string | null = null
): EmptyView => {
  if (list.total > 0) return shown === 0 ? 'filtered' : null;
  if (startFailure && (list.searchStatus === 'none' || list.searchStatus === undefined)) return 'failed';
  switch (list.searchStatus) {
    case 'running':
      return 'loading';
    case 'failed':
      return 'failed';
    case 'finished':
      return 'finished';
    case 'none':
      return 'notStarted';
    default:
      return list.importing ? 'loading' : 'none';
  }
};

export const FILTERS: { key: CandidateFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'wizListFilterAll' },
  { key: 'other', labelKey: 'wizListFilterOther' },
  { key: 'sex', labelKey: 'wizListFilterSex' },
  { key: 'blocked', labelKey: 'wizListFilterBlocked' },
];

export const initialsOf = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 3);

/** Position im gefilterten Feld, `(n / m)`. */
export const drawerPosition = (ids: string[], id: string | null): number =>
  id === null ? -1 : ids.indexOf(id);

/** Nächster/vorheriger Eintrag, zyklisch (Pfeile im Drawer). */
export const cycleId = (ids: string[], current: string | null, delta: 1 | -1): string | null => {
  if (ids.length === 0) return null;
  const pos = current === null ? -1 : ids.indexOf(current);
  if (pos < 0) return ids[0];
  return ids[(pos + delta + ids.length) % ids.length];
};

/** Motive-Netz: Mitte (80,70), Radius 46; Reihenfolge der Werte: oben, links, unten, rechts. */
export const motivePolygon = (motives: CandidatePersona['motives']): string => {
  const cx = 80;
  const cy = 70;
  const radius = 46;
  const [top, left, bottom, right] = motives;
  return [
    [cx, cy - radius * top],
    [cx + radius * right, cy],
    [cx, cy + radius * bottom],
    [cx - radius * left, cy],
  ]
    .map(point => point.map(n => n.toFixed(1)).join(','))
    .join(' ');
};

export interface MenuTarget {
  /** `campaign` oder Listen-ID. */
  target: string;
  name: string;
  meta: string;
  /** Anzeige-Variante der Meta-Zeile für die Übersetzung. */
  kind: 'campaign' | 'global';
  count?: number;
  off?: boolean;
  listType?: 'company' | 'contact';
}

/** Ziele im Menü „{Name} hinzufügen zu …“: Kampagnen-Blacklist + globale Listen passenden Typs. */
export const blacklistTargets = (
  scope: 'person' | 'company',
  lists: WizardBlacklistList[],
  globalBlacklists: WizardState['globalBlacklists']
): MenuTarget[] => {
  const type = scope === 'company' ? 'company' : 'contact';
  const targets: MenuTarget[] = [{ target: 'campaign', name: '', meta: '', kind: 'campaign' }];
  lists
    .filter(list => list.type === type)
    .forEach(list =>
      targets.push({
        target: list.id,
        name: list.name,
        meta: '',
        kind: 'global',
        count: list.count,
        off: (globalBlacklists[list.id] ?? list.defaultOn) === false,
        listType: list.type,
      })
    );
  return targets;
};

const sameText = (a: string, b: string): boolean =>
  a.trim().toLowerCase().replace(/\/$/, '') === b.trim().toLowerCase().replace(/\/$/, '');

/**
 * Spiegelt eine Blacklist-Aktion im Wizard-Zustand. Das Backend synchronisiert die
 * kampagnenlokalen Listen mit `blacklistCompanies`/`blacklistPeople`; stünde dort der alte Stand,
 * würde das nächste Speichern die Änderung wieder löschen.
 */
export const mirrorBlacklistAdd = (
  state: Pick<WizardState, 'blacklistCompanies' | 'blacklistPeople' | 'globalBlacklists'>,
  candidate: Pick<WizardCandidate, 'company' | 'linkedInUrl'>,
  scope: 'person' | 'company',
  target: string
): Pick<WizardState, 'blacklistCompanies' | 'blacklistPeople' | 'globalBlacklists'> => {
  if (target !== 'campaign') {
    return { ...state, globalBlacklists: { ...state.globalBlacklists, [target]: true } };
  }
  if (scope === 'company') {
    const exists = state.blacklistCompanies.some(name => sameText(name, candidate.company));
    return exists || !candidate.company.trim()
      ? state
      : { ...state, blacklistCompanies: [...state.blacklistCompanies, candidate.company] };
  }
  const exists = state.blacklistPeople.some(url => sameText(url, candidate.linkedInUrl));
  return exists || !candidate.linkedInUrl.trim()
    ? state
    : { ...state, blacklistPeople: [...state.blacklistPeople, candidate.linkedInUrl] };
};

export const mirrorBlacklistRemove = (
  state: Pick<WizardState, 'blacklistCompanies' | 'blacklistPeople'>,
  candidate: Pick<WizardCandidate, 'company' | 'linkedInUrl'>,
  scope: 'person' | 'company'
): Pick<WizardState, 'blacklistCompanies' | 'blacklistPeople'> =>
  scope === 'company'
    ? {
        ...state,
        blacklistCompanies: state.blacklistCompanies.filter(
          name => !sameText(name, candidate.company)
        ),
      }
    : {
        ...state,
        blacklistPeople: state.blacklistPeople.filter(url => !sameText(url, candidate.linkedInUrl)),
      };

/** Aktiv-Umschalter optimistisch auf die Liste anwenden. */
export const withActive = (
  items: WizardCandidate[],
  id: string,
  active: boolean
): WizardCandidate[] => items.map(item => (item.id === id ? { ...item, active } : item));

export const countActive = (items: WizardCandidate[]): number =>
  items.filter(item => item.active).length;

/** URL des Persona-Bildes: relative Pfade aus dem Backend hängen am Basispfad der App. */
export const personaImageUrl = (image: string | null, base = '/'): string | null => {
  if (!image) return null;
  if (/^(https?:)?\/\//.test(image) || image.startsWith('data:')) return image;
  return `${base.replace(/\/$/, '')}/${image.replace(/^\//, '')}`;
};

/* ------------------------------------------------------------------ */
/* Suchfilter der Tabelle (clientseitig über die geladenen Zeilen)      */
/* ------------------------------------------------------------------ */

export type StatusFilter = 'rated' | 'analysing' | 'waiting_quota' | 'failed' | 'excluded';

export const STATUS_FILTERS: { key: StatusFilter; labelKey: string }[] = [
  { key: 'rated', labelKey: 'wizListStatusRated' },
  { key: 'analysing', labelKey: 'wizListStatusAnalysing' },
  { key: 'waiting_quota', labelKey: 'wizListStatusQuota' },
  { key: 'failed', labelKey: 'wizListStatusFailed' },
  { key: 'excluded', labelKey: 'wizListStatusExcluded' },
];

export interface TableFilters {
  text: string;
  classes: Judgement[];
  statuses: StatusFilter[];
  /** '' = alle */
  location: string;
  company: string;
}

export const EMPTY_FILTERS: TableFilters = {
  text: '',
  classes: [],
  statuses: [],
  location: '',
  company: '',
};

export const hasTableFilters = (f: TableFilters): boolean =>
  f.text.trim() !== '' ||
  f.classes.length > 0 ||
  f.statuses.length > 0 ||
  f.location !== '' ||
  f.company !== '';

/** Status einer Zeile für den Filter. „Blacklist / nicht passend“ = blockiert oder Klasse C. */
export const statusOf = (
  c: Pick<WizardCandidate, 'blocked' | 'judgement' | 'ratingState'>
): StatusFilter[] => {
  const out: StatusFilter[] = [];
  if (c.blocked || c.judgement === 'C') out.push('excluded');
  if (c.ratingState === 'failed') out.push('failed');
  else if (c.ratingState === 'waiting_quota' || c.ratingState === 'waiting_linkedin') out.push('waiting_quota');
  else if (c.ratingState === 'analysing' || c.ratingState === 'pending') out.push('analysing');
  else if (c.judgement || c.ratingState === 'rated') out.push('rated');
  return out;
};

const norm = (s: string): string => s.trim().toLowerCase();

export const applyTableFilters = (items: WizardCandidate[], f: TableFilters): WizardCandidate[] => {
  const text = norm(f.text);
  return items.filter(c => {
    if (text && ![c.name, c.title, c.company, c.location].some(v => norm(v ?? '').includes(text)))
      return false;
    if (f.classes.length > 0 && (!c.judgement || !f.classes.includes(c.judgement))) return false;
    if (f.statuses.length > 0 && !statusOf(c).some(s => f.statuses.includes(s))) return false;
    if (f.location && norm(c.location ?? '') !== norm(f.location)) return false;
    if (f.company && norm(c.company ?? '') !== norm(f.company)) return false;
    return true;
  });
};

/** Vorhandene Werte (ohne Dubletten, ohne leere), alphabetisch – für die Auswahlfelder Ort und Firma. */
export const distinctValues = (items: WizardCandidate[], key: 'location' | 'company'): string[] => {
  const seen = new Map<string, string>();
  for (const item of items) {
    const value = (item[key] ?? '').trim();
    if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, 'de'));
};

/** Auswahl nach einem Filterwechsel: nur Zeilen behalten, die noch sichtbar sind. */
export const pruneSelection = (selected: string[], visible: WizardCandidate[]): string[] => {
  const ids = new Set(visible.map(c => c.id));
  return selected.filter(id => ids.has(id));
};

/** Alle auswählen wirkt nur auf die gefilterten Zeilen. */
export const toggleAll = (selected: string[], visible: WizardCandidate[]): string[] =>
  visible.length > 0 && visible.every(c => selected.includes(c.id)) ? [] : visible.map(c => c.id);

const csvCell = (value: unknown): string => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

/** CSV der gefilterten bzw. ausgewählten Zeilen (Excel: BOM, Semikolon, Formel-Schutz). */
export const candidatesCsv = (rows: WizardCandidate[]): string => {
  const header = [
    'Name',
    'Position',
    'Unternehmen',
    'Ort',
    'LinkedIn-URL',
    'Klasse (KI)',
    'Klasse (aktuell)',
    'Manuell angepasst',
    'Auf der Kandidatenliste',
    'Blockiert',
  ];
  const lines = [header.map(csvCell).join(';')];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.title,
        r.company,
        r.location,
        r.linkedInUrl,
        r.aiJudgement ?? '',
        r.judgement ?? '',
        r.manual ? 'ja' : 'nein',
        r.active ? 'ja' : 'nein',
        r.blocked ? `ja (${r.blockedBy.join(', ')})` : 'nein',
      ]
        .map(csvCell)
        .join(';')
    );
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
};

/** Link zum LinkedIn-Profil im Drawer: nur https auf linkedin.com (Daten kommen aus Suchergebnissen), sonst null. */
export const safeLinkedInUrl = (value: string | null | undefined): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== 'https:') return null;
    if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return null;
    return url.toString();
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Zeiteinschätzung der Profilanalyse (Herleitung: Backend `analysis-forecast.ts`)  */
/* ------------------------------------------------------------------ */

/** Restminuten bis `etaAt` (aufgerundet, nie negativ). Der Countdown tickt lokal, der Server rechnet bei jedem Abruf neu. */
export const forecastMinutesLeft = (forecast: Pick<WizardAnalysisForecast, 'etaAt'>, nowMs: number): number => {
  const eta = new Date(forecast.etaAt).getTime();
  if (Number.isNaN(eta)) return 0;
  return Math.max(0, Math.ceil((eta - nowMs) / 60_000));
};

/** „45 Min.“ bzw. „1 Std. 20 Min.“ */
export const formatMinutes = (
  minutes: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (minutes < 60) return t('wizListMinutes', { minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t('wizListHours', { hours })
    : t('wizListHoursMinutes', { hours, minutes: rest });
};

/** Gesamtzahl der Kandidaten, auf die sich die Einschätzung bezieht (geladen + noch zu ladende). */
export const forecastTotal = (f: Pick<WizardAnalysisForecast, 'todayCount' | 'restCount'>): number =>
  f.todayCount + f.restCount;

/**
 * Text der Zeiteinschätzung:
 *  - alles passt in den heutigen Durchlauf: „Noch ca. 25 Min. für 25 Kandidaten“
 *  - sonst „X heute (noch ca. N Min.), Rest ab <Datum>“
 *  - heute nichts mehr möglich: nur „Rest ab <Datum>“
 */
export const forecastText = (
  f: WizardAnalysisForecast,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  nowMs: number
): string => {
  const minutes = formatMinutes(forecastMinutesLeft(f, nowMs), t);
  const date = pauseDate(f.restStartsAt, language);
  if (f.todayCount > 0 && f.restCount === 0) {
    return t('wizListForecastAll', { count: f.todayCount, minutes });
  }
  if (f.todayCount > 0) {
    return t(date ? 'wizListForecastSplit' : 'wizListForecastSplitNoDate', {
      today: f.todayCount,
      minutes,
      rest: f.restCount,
      date,
    });
  }
  return t(date ? 'wizListForecastRest' : 'wizListForecastRestNoDate', { rest: f.restCount, date });
};

/** Hinweis „Tageslimit erreicht“: alles, was heute geht, ist erledigt, der Rest wartet. */
export const dailyRunDone = (
  list: Pick<WizardCandidateList, 'analysisPending' | 'analysisPause' | 'analysisForecast' | 'analysisQuotaExhausted'>
): boolean => {
  const f = list.analysisForecast;
  if (!f || list.analysisPause) return false;
  if ((list.analysisPending ?? 0) <= 0 || f.restCount <= 0) return false;
  return f.todayCount === 0 && (f.dailyLimitReached || list.analysisQuotaExhausted === true);
};

/** Schlüssel für „Popup schon gezeigt“: pro Kampagne und Berliner Kalendertag. */
export const limitPopupKey = (campaignId: string, nowMs: number): string => {
  const day = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(nowMs));
  return `saia-wizard-limit-popup:${campaignId}:${day}`;
};

type PopupStorage = Pick<Storage, 'getItem' | 'setItem'>;

const popupStorage = (): PopupStorage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export const wasLimitPopupShown = (key: string, storage: PopupStorage | null = popupStorage()): boolean => {
  try {
    return storage?.getItem(key) === '1';
  } catch {
    return false;
  }
};

export const markLimitPopupShown = (key: string, storage: PopupStorage | null = popupStorage()): void => {
  try {
    storage?.setItem(key, '1');
  } catch {
    /* ohne Speicher erscheint das Popup beim nächsten Laden erneut – akzeptabel */
  }
};

/**
 * DEV-1093: Empfehlung oben in Schritt 4 („Du musst nicht warten“). Sichtbar, solange die Suche läuft,
 * Profile noch analysiert werden oder die Analyse pausiert ist; weg, sobald alles analysiert und die Suche fertig ist.
 */
export const showSearchRecommendation = (
  list: Pick<WizardCandidateList, 'searchStatus' | 'importing' | 'analysisPending' | 'analysisPause'>
): boolean =>
  isSearchRunning(list) || (list.analysisPending ?? 0) > 0 || Boolean(list.analysisPause);
